import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  Alert,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Chip,
  useTheme,
  useMediaQuery,
  Stack,
  Collapse,
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  AccountBalance as AccountBalanceIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';

const CapitalManagement = () => {
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const [filtersOpen, setFiltersOpen] = useState(!isMobile); // Collapsed on mobile by default
  
  const [capital, setCapital] = useState({ totalCapital: 0, remainingCapital: 0 });
  const [transactions, setTransactions] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState('add'); // 'add', 'update', 'withdraw'
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [formData, setFormData] = useState({
    amount: '',
    description: '',
    areaId: '',
  });
  const [message, setMessage] = useState('');
  const [startDate, setStartDate] = useState(''); // From date filter
  const [endDate, setEndDate] = useState(''); // To date filter
  const [selectedArea, setSelectedArea] = useState(''); // Area filter
  const [areas, setAreas] = useState([]);

  useEffect(() => {
    fetchAreas();
  }, []);

  useEffect(() => {
    fetchCapital();
    fetchTransactions();
    
    // Auto-refresh every 5 seconds for real-time updates
    const interval = setInterval(() => {
      fetchCapital();
      fetchTransactions();
    }, 5000);
    
    return () => clearInterval(interval);
  }, [startDate, endDate, selectedArea]);
  
  const fetchAreas = async () => {
    try {
      const response = await api.get('/admin/areas');
      setAreas(response.data || []);
    } catch (error) {
      console.error('Error fetching areas:', error);
    }
  };

  const fetchCapital = async () => {
    try {
      const params = {};
      if (selectedArea) {
        params.areaId = selectedArea;
      }
      const response = await api.get('/admin/capital/current', { params });
      setCapital(response.data);
    } catch (error) {
      console.error('Error fetching capital:', error);
    }
  };

  const fetchTransactions = async () => {
    try {
      const params = {};
      
      // Add area filter if selected
      if (selectedArea) {
        params.areaId = selectedArea;
      }
      
      // Use date range if both dates are provided
      if (startDate && endDate) {
        params.startDate = startDate;
        params.endDate = endDate;
        const response = await api.get('/admin/capital/transactions/range', { params });
        setTransactions(response.data);
      } else if (startDate) {
        // If only start date is provided, use it as single date filter
        params.date = startDate;
        const response = await api.get('/admin/capital/transactions', { params });
        setTransactions(response.data);
      } else {
        // No date filter - get all transactions
        const response = await api.get('/admin/capital/transactions', { params });
        setTransactions(response.data);
      }
    } catch (error) {
      console.error('Error fetching transactions:', error);
    }
  };

  const handleOpenDialog = (type, transaction = null) => {
    setDialogType(type);
    setSelectedTransaction(transaction);
    setFormData({
      amount: transaction ? transaction.amount : '',
      description: transaction ? transaction.description : '',
      areaId: selectedArea || '',
    });
    setOpenDialog(true);
    setMessage('');
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setFormData({ amount: '', description: '', areaId: '' });
    setSelectedTransaction(null);
  };

  const handleSubmit = async () => {
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      setMessage('Please enter a valid amount');
      return;
    }

    // Require area selection for add and withdraw operations
    if ((dialogType === 'add' || dialogType === 'withdraw') && !formData.areaId) {
      setMessage('Please select an area');
      return;
    }

    try {
      const params = {
        amount: parseFloat(formData.amount),
        adminId: user.userId,
      };
      if (formData.description) {
        params.description = formData.description;
      }
      if (formData.areaId) {
        params.areaId = formData.areaId;
      }

      if (dialogType === 'add') {
        await api.post('/admin/capital/add', null, { params });
        setMessage('Capital added successfully!');
      } else if (dialogType === 'update') {
        await api.put(`/admin/capital/${selectedTransaction.id}`, null, { params });
        setMessage('Capital updated successfully!');
      } else if (dialogType === 'withdraw') {
        await api.post('/admin/capital/withdraw', null, { params });
        setMessage('Capital withdrawn successfully!');
      }

      fetchCapital();
      fetchTransactions();
      handleCloseDialog();
    } catch (error) {
      setMessage('Error: ' + (error.response?.data?.error || error.message));
    }
  };

  const getTransactionTypeColor = (type) => {
    switch (type) {
      case 'ADDITION':
        return 'success';
      case 'WITHDRAWAL':
        return 'error';
      case 'UPDATE':
        return 'info';
      default:
        return 'default';
    }
  };

  return (
    <Box sx={{ px: { xs: 1, sm: 2, md: 3 }, py: { xs: 2, sm: 3 } }}>
      <Typography 
        variant="h4" 
        gutterBottom
        sx={{ 
          fontWeight: 700,
          color: '#0D47A1',
          mb: { xs: 3, sm: 4 },
          fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
          textShadow: '0 2px 4px rgba(13, 71, 161, 0.2)',
        }}
      >
        Capital Management
      </Typography>

      {/* Capital Overview Cards */}
      <Grid container spacing={{ xs: 2, sm: 2 }} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card 
            sx={{ 
              background: 'linear-gradient(135deg, #0D47A1 0%, #1565C0 100%)',
              color: 'white',
              boxShadow: '0 4px 20px rgba(13, 71, 161, 0.4)',
              borderRadius: 2,
              transition: 'transform 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 8px 30px rgba(13, 71, 161, 0.5)',
              },
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, flexWrap: 'wrap' }}>
                <AccountBalanceIcon sx={{ mr: 1, fontSize: { xs: 32, sm: 40 } }} />
                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                  {selectedArea ? areas.find(a => a.id === parseInt(selectedArea))?.name + ' Capital' : 'Total Capital'}
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 700, fontSize: { xs: '1.5rem', sm: '2rem' }, wordBreak: 'break-word' }}>
                Rs {parseFloat(capital.totalCapital || 0).toFixed(2)}
              </Typography>
              {selectedArea && (
                <Typography variant="body2" sx={{ mt: 1, opacity: 0.9, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                  Capital for selected area
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card 
            sx={{ 
              background: 'linear-gradient(135deg, #2E7D32 0%, #1B5E20 100%)',
              color: 'white',
              boxShadow: '0 4px 20px rgba(46, 125, 50, 0.3)',
              borderRadius: 2,
              transition: 'transform 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 8px 30px rgba(46, 125, 50, 0.4)',
              },
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, flexWrap: 'wrap' }}>
                <AccountBalanceIcon sx={{ mr: 1, fontSize: { xs: 32, sm: 40 } }} />
                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '0.9rem', sm: '1rem' } }}>Remaining Capital</Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 700, fontSize: { xs: '1.5rem', sm: '2rem' }, wordBreak: 'break-word' }}>
                Rs {parseFloat(capital.remainingCapital || 0).toFixed(2)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Action Buttons */}
      <Paper 
        sx={{ 
          p: { xs: 2, sm: 2 }, 
          mb: 3,
          borderRadius: 2,
          boxShadow: '0 4px 20px rgba(13, 71, 161, 0.1)',
        }}
      >
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <Button
            fullWidth={isMobile}
            variant="contained"
            color="success"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog('add')}
            sx={{
              background: 'linear-gradient(135deg, #0D47A1 0%, #1565C0 100%)',
              boxShadow: '0 4px 15px rgba(13, 71, 161, 0.4)',
              color: 'white',
              '&:hover': {
                background: 'linear-gradient(135deg, #1565C0 0%, #1976D2 100%)',
                boxShadow: '0 6px 20px rgba(13, 71, 161, 0.5)',
              },
              py: { xs: 1.5, sm: 1 },
            }}
          >
            Add Capital
          </Button>
          <Button
            fullWidth={isMobile}
            variant="contained"
            startIcon={<RemoveIcon />}
            onClick={() => handleOpenDialog('withdraw')}
            sx={{
              background: 'linear-gradient(135deg, #0D47A1 0%, #1565C0 100%)',
              boxShadow: '0 4px 15px rgba(13, 71, 161, 0.4)',
              color: 'white',
              '&:hover': {
                background: 'linear-gradient(135deg, #1565C0 0%, #1976D2 100%)',
                boxShadow: '0 6px 20px rgba(13, 71, 161, 0.5)',
              },
              py: { xs: 1.5, sm: 1 },
            }}
          >
            Withdraw Capital
          </Button>
        </Stack>
      </Paper>

      {message && (
        <Alert severity={message.includes('Error') ? 'error' : 'success'} sx={{ mb: 2 }}>
          {message}
        </Alert>
      )}

      {/* Transactions Table */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
            Capital Transactions
          </Typography>
          {isMobile && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => setFiltersOpen(!filtersOpen)}
            >
              {filtersOpen ? 'Hide Filters' : 'Show Filters'}
            </Button>
          )}
        </Box>
        <Collapse in={filtersOpen || !isMobile}>
          <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 2, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  select
                  label="Filter by Area"
                  value={selectedArea}
                  onChange={(e) => setSelectedArea(e.target.value)}
                  size="small"
                >
                  <MenuItem value="">All Areas</MenuItem>
                  {areas.map((area) => (
                    <MenuItem key={area.id} value={area.id}>
                      {area.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="From Date"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                  inputProps={{
                    max: endDate || undefined
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="To Date"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                  inputProps={{
                    min: startDate || undefined
                  }}
                  error={startDate && endDate && endDate < startDate}
                  helperText={startDate && endDate && endDate < startDate ? 'To date must be after from date' : ''}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                {(startDate || endDate || selectedArea) && (
                  <Button
                    fullWidth
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      setStartDate('');
                      setEndDate('');
                      setSelectedArea('');
                    }}
                    sx={{ minHeight: '40px' }}
                  >
                    Clear Filters
                  </Button>
                )}
              </Grid>
            </Grid>
          </Paper>
        </Collapse>
      </Box>
      {/* Mobile Card View */}
      {isMobile && (
        <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
          {transactions.length === 0 ? (
            <Paper sx={{ p: 3, textAlign: 'center', borderRadius: 2 }}>
              <Typography variant="body2" color="text.secondary">
                No transactions found
              </Typography>
            </Paper>
          ) : (
            <Stack spacing={2}>
              {transactions.map((transaction) => (
                <Card
                  key={transaction.id}
                  sx={{
                    borderRadius: 2,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: '0 4px 12px rgba(13, 71, 161, 0.2)',
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block', mb: 0.5 }}>
                          Date
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                          {new Date(transaction.createdAt).toLocaleDateString()}
                        </Typography>
                      </Box>
                      <Chip
                        label={transaction.transactionType}
                        color={getTransactionTypeColor(transaction.transactionType)}
                        size="small"
                        sx={{ fontSize: '0.65rem', height: 24 }}
                      />
                    </Box>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
                      <Box sx={{ flex: 1, minWidth: '120px' }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block', mb: 0.5 }}>
                          Amount
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#0D47A1' }}>
                          Rs {parseFloat(transaction.amount || 0).toFixed(2)}
                        </Typography>
                      </Box>
                      {transaction.area && (
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block', mb: 0.5 }}>
                            Area
                          </Typography>
                          <Chip
                            label={transaction.area.name}
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{ fontSize: '0.65rem', height: 24 }}
                          />
                        </Box>
                      )}
                    </Box>

                    {/* Description - Always visible on mobile */}
                    <Box sx={{ mb: 1.5, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block', mb: 0.5, fontWeight: 600 }}>
                        Description
                      </Typography>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontSize: '0.8rem', 
                          color: transaction.description ? 'text.primary' : 'text.secondary',
                          wordBreak: 'break-word',
                          lineHeight: 1.5,
                        }}
                      >
                        {transaction.description || '-'}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block', mb: 0.5 }}>
                          Total Capital
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 500 }}>
                          Rs {parseFloat(transaction.totalCapital || 0).toFixed(2)}
                        </Typography>
                      </Box>
                      {transaction.addedBy && (
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block', mb: 0.5 }}>
                            Added By
                          </Typography>
                          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                            {transaction.addedBy.fullName}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          )}
        </Box>
      )}

      {/* Desktop/Tablet Table View */}
      <TableContainer 
        component={Paper}
        sx={{
          borderRadius: 2,
          boxShadow: '0 4px 20px rgba(13, 71, 161, 0.1)',
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
          display: { xs: 'none', sm: 'block' },
          '&::-webkit-scrollbar': {
            height: 8,
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#0D47A1',
            borderRadius: 4,
          },
        }}
      >
        <Table size={isTablet ? 'small' : 'medium'}>
          <TableHead>
            <TableRow sx={{ background: 'linear-gradient(135deg, #0D47A1 0%, #1565C0 100%)' }}>
              <TableCell sx={{ color: 'white', fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' }, py: { xs: 1, sm: 1.5 }, whiteSpace: 'nowrap' }}>Date</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' }, py: { xs: 1, sm: 1.5 }, whiteSpace: 'nowrap' }}>Type</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' }, py: { xs: 1, sm: 1.5 }, whiteSpace: 'nowrap' }}>Area</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' }, py: { xs: 1, sm: 1.5 }, whiteSpace: 'nowrap' }}>Amount</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' }, py: { xs: 1, sm: 1.5 }, whiteSpace: 'nowrap' }}>Total Capital</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' }, py: { xs: 1, sm: 1.5 }, whiteSpace: 'nowrap', minWidth: { sm: '150px', md: '200px' } }}>Description</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' }, py: { xs: 1, sm: 1.5 }, whiteSpace: 'nowrap', display: { xs: 'none', lg: 'table-cell' } }}>Added By</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {transactions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    No transactions found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              transactions.map((transaction) => (
                <TableRow key={transaction.id} hover>
                  <TableCell sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' }, py: { xs: 1, sm: 1.5 }, whiteSpace: 'nowrap' }}>
                    {new Date(transaction.createdAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' }, py: { xs: 1, sm: 1.5 } }}>
                    <Chip
                      label={transaction.transactionType}
                      color={getTransactionTypeColor(transaction.transactionType)}
                      size="small"
                      sx={{ fontSize: { xs: '0.6rem', sm: '0.75rem' } }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' }, py: { xs: 1, sm: 1.5 } }}>
                    {transaction.area ? (
                      <Chip
                        label={transaction.area.name}
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ fontSize: { xs: '0.6rem', sm: '0.75rem' } }}
                      />
                    ) : (
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>-</Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' }, py: { xs: 1, sm: 1.5 }, whiteSpace: 'nowrap' }}>
                    <strong>Rs {parseFloat(transaction.amount || 0).toFixed(2)}</strong>
                  </TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' }, py: { xs: 1, sm: 1.5 }, whiteSpace: 'nowrap' }}>
                    Rs {parseFloat(transaction.totalCapital || 0).toFixed(2)}
                  </TableCell>
                  <TableCell sx={{ 
                    fontSize: { xs: '0.7rem', sm: '0.875rem' }, 
                    py: { xs: 1, sm: 1.5 },
                    maxWidth: { sm: '200px', md: '300px' },
                    wordBreak: 'break-word',
                    whiteSpace: 'normal',
                  }}>
                    {transaction.description || '-'}
                  </TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' }, py: { xs: 1, sm: 1.5 }, display: { xs: 'none', lg: 'table-cell' } }}>{transaction.addedBy?.fullName || '-'}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Update/Withdraw Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog} 
        maxWidth="sm" 
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: { xs: 0, sm: 2 },
            m: { xs: 0, sm: 2 },
            maxHeight: { xs: '100%', sm: '90vh' },
          },
        }}
      >
        <DialogTitle>
          {dialogType === 'add' && 'Add Capital'}
          {dialogType === 'update' && 'Update Capital'}
          {dialogType === 'withdraw' && 'Withdraw Capital'}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Amount"
            type="number"
            value={formData.amount}
            onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
            sx={{ mt: 2, mb: 2 }}
            required
            inputProps={{ min: 0, step: 0.01 }}
          />
          <TextField
            fullWidth
            select
            label={dialogType === 'add' || dialogType === 'withdraw' ? "Area *" : "Area (Optional)"}
            value={formData.areaId || ''}
            onChange={(e) => setFormData({ ...formData, areaId: e.target.value })}
            sx={{ mt: 2, mb: 2 }}
            required={dialogType === 'add' || dialogType === 'withdraw'}
            error={(dialogType === 'add' || dialogType === 'withdraw') && !formData.areaId}
            helperText={(dialogType === 'add' || dialogType === 'withdraw') && !formData.areaId ? 'Area is required' : ''}
          >
            {dialogType !== 'add' && dialogType !== 'withdraw' && (
              <MenuItem value="">No Specific Area</MenuItem>
            )}
            {areas.map((area) => (
              <MenuItem key={area.id} value={area.id}>
                {area.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth
            label="Description"
            multiline
            rows={3}
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Enter description for this transaction..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {dialogType === 'add' && 'Add'}
            {dialogType === 'update' && 'Update'}
            {dialogType === 'withdraw' && 'Withdraw'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CapitalManagement;

